import apiEndPoints from '../../../routeConfig';
import { ApiService } from '../../../../service/api.service';
import { attempt, resultMessage } from '../../../../service/apiResult';

/**
 * Cropping season calendars, per country.
 *
 * eK Score keys a rainfall baseline to a season, and SeasonResolver decides which season a date
 * falls in by loading the country's calendar from risk.season_calendars. A country with no calendar
 * falls back to the engine's built-in India heuristic (Kharif Jun–Oct, Zaid Apr–May, Rabi
 * otherwise) — which is wrong for Ghana, whose zones run on a unimodal or bimodal calendar, and is
 * the reason this is configurable at all.
 *
 * Saving here changes how that country's farmers are scored. Clearing a calendar is a legitimate
 * action, not a broken state: it puts the country back on the built-in seasons.
 *
 * Seasons are whole months, inclusive, and may wrap the new year (Rabi is Nov–Mar), because that
 * is exactly the granularity seasonCodeFor works at. A calendar must tile the year: every month in
 * exactly one season, or a date maps to two seasons or none.
 */

const apiService = new ApiService();

const body = (result) => result?.data?.data ?? result?.data ?? null;
const ok = (data) => ({ success: true, data });
const fail = (message) => ({ success: false, error: { message } });

/** The API speaks seasonCode; the screen has always spoken code. Translated at the boundary. */
const fromApi = (row) => ({
  id: row.id,
  code: row.seasonCode,
  label: row.label,
  startMonth: row.startMonth,
  endMonth: row.endMonth,
  notes: row.notes || '',
});

const toApi = (season) => ({
  seasonCode: season.code,
  label: season.label,
  startMonth: Number(season.startMonth),
  endMonth: Number(season.endMonth),
  notes: season.notes || null,
});

export const MONTHS = [
  { value: 1, label: 'January', short: 'Jan' },
  { value: 2, label: 'February', short: 'Feb' },
  { value: 3, label: 'March', short: 'Mar' },
  { value: 4, label: 'April', short: 'Apr' },
  { value: 5, label: 'May', short: 'May' },
  { value: 6, label: 'June', short: 'Jun' },
  { value: 7, label: 'July', short: 'Jul' },
  { value: 8, label: 'August', short: 'Aug' },
  { value: 9, label: 'September', short: 'Sep' },
  { value: 10, label: 'October', short: 'Oct' },
  { value: 11, label: 'November', short: 'Nov' },
  { value: 12, label: 'December', short: 'Dec' },
];

export function monthShort(value) {
  return MONTHS.find((month) => month.value === Number(value))?.short || '—';
}

/**
 * The calendar the engine currently applies to every country, offered as a starting point so a new
 * country begins from what is actually in force rather than from nothing.
 */
export const ENGINE_DEFAULT_SEASONS = [
  { code: 'KHARIF', label: 'Kharif', startMonth: 6, endMonth: 10 },
  { code: 'RABI', label: 'Rabi', startMonth: 11, endMonth: 3 },
  { code: 'ZAID', label: 'Zaid', startMonth: 4, endMonth: 5 },
];



/** The months a season covers, wrapping the new year when it ends before it starts. */
export function monthsCovered(season) {
  const start = Number(season?.startMonth);
  const end = Number(season?.endMonth);
  if (!(start >= 1 && start <= 12) || !(end >= 1 && end <= 12)) return [];

  const months = [];
  let month = start;
  // Bounded at 12 steps so a wrapping season terminates rather than looping forever.
  for (let i = 0; i < 12; i += 1) {
    months.push(month);
    if (month === end) break;
    month = month === 12 ? 1 : month + 1;
  }
  return months;
}

export function spansNewYear(season) {
  return Number(season?.endMonth) < Number(season?.startMonth);
}

/** The first day of a season, matching what seasonStartFor uses to open the rainfall window. */
export function seasonStartLabel(season) {
  return `1 ${monthShort(season?.startMonth)}`;
}

/**
 * Which season owns each month, and where the calendar is broken.
 *
 * A month claimed twice means a date resolves to two seasons; a month claimed by none means there
 * is no season for that date at all. Both are blocking, and both are properties of the whole
 * calendar rather than of any one season, so they are computed together.
 */
export function coverage(seasons = []) {
  const byMonth = {};
  MONTHS.forEach((month) => {
    byMonth[month.value] = [];
  });

  seasons.forEach((season) => {
    monthsCovered(season).forEach((month) => {
      byMonth[month].push(season.code || '—');
    });
  });

  const uncovered = MONTHS.filter((month) => byMonth[month.value].length === 0).map((m) => m.value);
  const overlapping = MONTHS.filter((month) => byMonth[month.value].length > 1).map((m) => m.value);

  return { byMonth, uncovered, overlapping, complete: !uncovered.length && !overlapping.length };
}

export function validateSeason(draft, existing = []) {
  const issues = {};
  const code = String(draft.code || '').trim();

  if (!code) {
    issues.code = 'Enter a season code.';
  } else if (!/^[A-Z0-9_]+$/.test(code)) {
    // This code is what a rainfall baseline stores in season_code, so it keeps the shape the rest
    // of the platform already uses for codes.
    issues.code = 'Use capitals, numbers and underscores only, like MAJOR_RAINS.';
  } else if (existing.some((season) => season.code === code && season.code !== draft.originalCode)) {
    issues.code = 'This country already has a season with that code.';
  }

  if (!String(draft.label || '').trim()) {
    issues.label = 'Enter a display name.';
  }

  const start = Number(draft.startMonth);
  const end = Number(draft.endMonth);
  if (!(start >= 1 && start <= 12)) issues.startMonth = 'Choose a start month.';
  if (!(end >= 1 && end <= 12)) issues.endMonth = 'Choose an end month.';

  return issues;
}

export function validateCalendar(seasons = []) {
  const issues = [];
  if (!seasons.length) {
    issues.push('Add at least one season.');
    return issues;
  }

  const { uncovered, overlapping } = coverage(seasons);
  if (uncovered.length) {
    issues.push(
      `No season covers ${uncovered.map(monthShort).join(', ')} — a date in those months has no season.`
    );
  }
  if (overlapping.length) {
    issues.push(
      `More than one season covers ${overlapping.map(monthShort).join(', ')} — a date there is ambiguous.`
    );
  }
  return issues;
}

/* ---------------------------------------------------------------- storage */

export async function getSeasons(countryId) {
  if (!countryId) return ok([]);

  const result = await attempt(() => apiService.get(`${apiEndPoints.seasonCalendars}?countryId=${countryId}`));
  if (!result.success) return fail(resultMessage(result, 'Could not load the season calendar.'));

  return ok((body(result) || []).map(fromApi));
}


export async function saveSeasons(countryId, seasons) {
  if (!countryId) return fail('Choose a country first.');

  const issues = validateCalendar(seasons);
  if (issues.length) return fail(issues[0]);

  const result = await attempt(() => apiService.update(apiEndPoints.seasonCalendars, {
    countryId,
    seasons: seasons.map(toApi),
  }));
  if (!result.success) return fail(resultMessage(result, 'Could not save the calendar.'));

  return ok((body(result) || []).map(fromApi));
}

export async function resetToEngineDefault(countryId) {
  return saveSeasons(countryId, ENGINE_DEFAULT_SEASONS.map((season) => ({ ...season })));
}

export async function clearSeasons(countryId) {
  const result = await attempt(() =>
    apiService.delete(`${apiEndPoints.seasonCalendars}?countryId=${countryId}`));
  if (!result.success) return fail(resultMessage(result, 'Could not clear the calendar.'));

  return ok([]);
}

/**
 * Season options for a country, falling back to the engine's own calendar.
 *
 * The fallback matters: a rainfall baseline still has to be keyed to whatever season the engine
 * will actually pick, so offering nothing for an unconfigured country would be worse than offering
 * what is really in force.
 */
export async function getSeasonOptionsForCountry(countryId) {
  const result = await getSeasons(countryId);
  const configured = result.data || [];
  return configured.length
    ? { seasons: configured, configured: true }
    : { seasons: ENGINE_DEFAULT_SEASONS, configured: false };
}
